import { useState } from "react";
import Hero from "../components/Hero";
import "./InternshipPage.css";
import INTERN from "../assets/images/education/internship-filler1.jpg";
import { Link } from "react-router-dom";
import { useCompanyDetails } from "../hooks/useCompanyDetails";
import { DETAILS_LEAD, DETAILS_REST } from "../utils/internshipPage.helpers";

function ReadMoreToggle({
  expanded,
  onToggle,
  className,
}: {
  expanded: boolean;
  onToggle: () => void;
  className?: string;
}) {
  return (
    <button
      type="button"
      className={`internship-read-more${className ? ` ${className}` : ""}`}
      onClick={onToggle}
      aria-expanded={expanded}
    >
      {expanded ? "Read less" : "Read more"}
    </button>
  );
}

function DetailRow({ label, value }: { label: string; value: string }) {
  return (
    <li className="internship-detail-row">
      <span className="internship-detail-row__label">{label}</span>
      <span className="internship-detail-row__value">{value}</span>
    </li>
  );
}

const InternshipPage = () => {
  const { data: companyDetails } = useCompanyDetails();
  const companyEmailAddress = companyDetails?.companyEmailAddress?.trim();
  const [isIntroExpanded, setIsIntroExpanded] = useState(false);
  const [isDetailsExpanded, setIsDetailsExpanded] = useState(false);
  const [isAcademicExpanded, setIsAcademicExpanded] = useState(false);

  return (
    <>
      <Hero page="internship" titleTwo="Internships" />
      <div className="content-container internship-container">
        <section
          className="wrapper-card green internship-intro-card"
          id="internship-intro"
        >
          <h3>Our Internships</h3>
          <div className="internship-intro">
            <img
              src={INTERN}
              alt="Group of interns celebrating"
              className="internship-intro__image"
            />
            <div className="internship-intro__content">
              <p className="internship-intro__lead">
                Develop practical agriculture skills with a focus on agroecology
                — from seeding and harvesting to crop planning, sales, and
                customer interaction on the farm.
              </p>

              <div
                className={
                  isIntroExpanded
                    ? "internship-intro__more is-expanded"
                    : "internship-intro__more"
                }
              >
                <p>
                  During the internship you will be involved in different
                  aspects of farm operations and learn building and
                  self-sufficiency skills. We teach ecological, social, and
                  economical sustainability from an agroecological perspective.
                </p>
                <p>
                  You can choose which activities to focus on to optimise your
                  learning, and carry out a personal project for research or
                  experimentation.
                </p>
              </div>

              {!isIntroExpanded && (
                <div className="internship-intro-mobile-toggle">
                  <ReadMoreToggle
                    expanded={false}
                    onToggle={() => setIsIntroExpanded(true)}
                  />
                </div>
              )}

              {isIntroExpanded && (
                <ReadMoreToggle
                  expanded
                  onToggle={() => setIsIntroExpanded(false)}
                  className="internship-intro-read-less"
                />
              )}
            </div>
          </div>
        </section>

        <section className="wrapper-card yellow internship-details-section">
          <div className="internship-details-panel info-card">
            <h3 className="internship-details-panel__title">
              Details & Conditions
            </h3>

            <ul className="internship-detail-rows">
              {DETAILS_LEAD.map((item) => (
                <DetailRow key={item.label} {...item} />
              ))}
            </ul>

            {!isDetailsExpanded && (
              <div className="internship-details-mobile-toggle">
                <ReadMoreToggle
                  expanded={false}
                  onToggle={() => setIsDetailsExpanded(true)}
                />
              </div>
            )}

            <div
              className={
                isDetailsExpanded
                  ? "internship-details-rest is-expanded"
                  : "internship-details-rest"
              }
            >
              <ul className="internship-detail-rows">
                {DETAILS_REST.map((item) => (
                  <DetailRow key={item.label} {...item} />
                ))}
              </ul>

              <p className="internship-disclaimer bold">
                This is hands-on, physical work. A good level of fitness helps
                with daily tasks, though we do our best to accommodate different
                skills and abilities.
              </p>

              {isDetailsExpanded && (
                <ReadMoreToggle
                  expanded
                  onToggle={() => setIsDetailsExpanded(false)}
                  className="internship-details-read-less"
                />
              )}
            </div>

            <div className="internship-types">
              <div className="internship-type">
                <h4>Independent Internship</h4>
                <p>
                  Not connected to an academic program and does not provide
                  university credits. For graduates or anyone who wants
                  practical experience — same program as our academic
                  internships.
                </p>
              </div>
              <div className="internship-type">
                <h4>Academic Internship</h4>
                <p>
                  If your university requires an internship, our program is
                  usually recognised and can provide academic credits. Length
                  and period can be adjusted to your university&apos;s
                  guidelines.
                </p>
                {isAcademicExpanded && (
                  <p>
                    SLU students have many opportunities to earn credits at
                    Alnarp&apos;s Farm throughout the year, including summer.
                    The free-standing course Advanced Practice is open to all
                    students in every period.
                  </p>
                )}
                <ReadMoreToggle
                  expanded={isAcademicExpanded}
                  onToggle={() => setIsAcademicExpanded((open) => !open)}
                />
              </div>
            </div>

            <div className="internship-details-footer">
              {companyEmailAddress && (
                <p>
                  Questions?{" "}
                  <a href={`mailto:${companyEmailAddress}`}>
                    {companyEmailAddress}
                  </a>
                </p>
              )}
              <Link
                to="/education/internship-application"
                className="btn internship-apply-btn"
              >
                Apply here
              </Link>
            </div>
          </div>
        </section>

        <section className="internship-practices-section">
          <div className="wrapper-card pink">
            <div className="practices-wrapper info-card">
              <div className="practices-container">
                <div className="practice-container">
                  <h3>Advanced Practice</h3>
                  <p className="center">
                    Educational level:{" "}
                    <span className="bold capitalized">MASTER</span>
                  </p>
                  <p>
                    This course can be taken in the Spring, Autumn and Summer
                    periods. You find out more about the exact course dates and
                    how to apply here on SLU's website
                  </p>
                  <a
                    href="https://www.universityadmissions.se/intl/search?period=14&freeText=advanced%20practice&sortBy=nameAsc"
                    className="btn"
                  >
                    Check out course
                  </a>
                </div>
                <div className="practice-container">
                  <h3>Horticultural Practice</h3>
                  <p className="center">
                    Educational level:{" "}
                    <span className="bold capitalized">BACHELOR</span>
                  </p>
                  <p>
                    This course can be taken in the Spring, Autumn and Summer
                    periods. You find out more about the exact course dates and
                    how to apply here on SLU's website
                  </p>
                  <a
                    href="https://www.slu.se/en/student-web/studies/courses-and-programmes/course-search/kurser/h/horticultural-practice2/"
                    className="btn"
                  >
                    Check out course
                  </a>
                </div>
              </div>
              <div className="practices-info">
                <p>
                  If you wish to apply for an academic internship through SLU,
                  you must complete both the regular application on our website
                  and the official SLU course application.
                </p>
              </div>
            </div>
          </div>
        </section>
      </div>
    </>
  );
};

export default InternshipPage;
